/**
 * Record a check-in held outside the system (HR UAT list #24).
 *
 * An HRBP (or HR admin) records the outcome of a Mid-Year or Year-End check-in
 * that happened outside the system, on the People Leader's behalf: the overall
 * rating, the KPA ratings and the justification, and completes it. It can be
 * recorded again to update it, until the person is submitted to calibration;
 * after that, changes go through calibration. Every save is logged with who
 * recorded it and why, and the check-in tells the People Leader and the Team
 * Member that HR recorded it.
 */
import React, { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardCheck, Loader2, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { EmployeePicker } from '@/components/performance/feedback/EmployeePicker'
import { reviewService, type KPARatingData, type ReviewSessionData } from '@/services/api/reviewService'
import { EDUVOS_KPAS, namesKpa } from '@/lib/kpaAliases'

interface PickedEmployee { id: string; name: string; role: string; department: string }

const RATINGS = [
  { value: 'exceptional', label: 'Exceptional' },
  { value: 'full', label: 'Full' },
  { value: 'not_yet_full', label: 'Not Yet Full' },
  { value: 'weak', label: 'Weak' },
  { value: 'tstr', label: 'Too Soon To Rate' },
]
// The four KPAs (lib/kpaAliases): the table name (the key a rating goes under when the
// check-in has no row for the KPA yet) and the display name. A check-in's rows are keyed by
// either, or a slug of the display name, depending on the screen that saved them (namesKpa).
const KPAS = EDUVOS_KPAS
const PERIOD: Record<string, string> = { midyear: 'Mid-Year', 'mid-year': 'Mid-Year', yearend: 'Year-End', 'year-end': 'Year-End' }

const norm = (v?: string | null) => (v || '').toLowerCase().replace(/-/g, '_')

/**
 * What the check-in already holds for each KPA (HR UAT list #24): the rating to show,
 * and the keys a new rating is saved under. A KPA can have more than one row, as each
 * screen keys it its own way. The rating shown is a rated row's, preferring the one the
 * completed check-in's snapshot names (what calibration reads). A new rating goes onto
 * every row the KPA already has, so they can't disagree, and under the key the snapshot
 * names when no row answers to it; a KPA with neither gets its table name.
 *
 * Saving rates the first row that answers to a key (its kpa_name or kpa_id), so each row
 * goes under a key only it answers to: its name, or its id when another row shares the
 * name (13 Oct 2026). A row with neither to itself keeps its name, as before.
 */
function recordedKpas(session: ReviewSessionData) {
  const rows = session.kpa_ratings || []
  const snapshot: { id?: string; name?: string; display_name?: string }[] =
    Array.isArray(session.kpa_data_snapshot) ? session.kpa_data_snapshot : []
  const answersTo = (r: KPARatingData, key?: string | null) => !!key && (r.kpa_name === key || r.kpa_id === key)
  const keyFor = (r: KPARatingData) =>
    [r.kpa_name, r.kpa_id].find((key) => rows.filter((other) => answersTo(other, key)).length === 1) ?? (r.kpa_name || r.kpa_id)
  const ratings: Record<string, string> = {}
  const keys: Record<string, string[]> = {}
  for (const kpa of KPAS) {
    const named = snapshot.find((s) => namesKpa(kpa, s.name) || namesKpa(kpa, s.display_name) || namesKpa(kpa, s.id))?.name
    const own = rows.filter((r) => namesKpa(kpa, r.kpa_name) || namesKpa(kpa, r.kpa_id) || answersTo(r, named))
    const rated = own.find((r) => r.rating && answersTo(r, named)) ?? own.find((r) => r.rating)
    if (rated?.rating) ratings[kpa.key] = norm(rated.rating)
    const unanswered = named && !own.some((r) => answersTo(r, named)) ? [named] : []
    keys[kpa.key] = Array.from(new Set([...own.map(keyFor), ...unanswered].filter((k): k is string => !!k)))
  }
  return { ratings, keys }
}

function RatingSelect({ id, value, onChange, label }: { id: string; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <label className="block text-sm" htmlFor={id}>
      <span className="mb-1 block font-medium text-gray-700">{label}</span>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm">
        <option value="">Not rated</option>
        {RATINGS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
    </label>
  )
}

const RecordCheckinOnBehalfAdmin: React.FC = () => {
  const currentYear = new Date().getFullYear()
  const [employee, setEmployee] = useState<PickedEmployee | null>(null)
  const [year, setYear] = useState(currentYear)
  const [target, setTarget] = useState<ReviewSessionData | null>(null)
  const [overall, setOverall] = useState('')
  const [kpas, setKpas] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('')
  const [complete, setComplete] = useState(true)
  const [saving, setSaving] = useState(false)
  // The keys each KPA's rating is saved under, and whether its recorded ratings loaded.
  const [saveKeys, setSaveKeys] = useState<Record<string, string[]>>({})
  const [kpaLoad, setKpaLoad] = useState<'loading' | 'ready' | 'unavailable'>('ready')
  const opening = useRef<string | null>(null)

  const { data: sessions, isLoading, refetch } = useQuery({
    queryKey: ['record-on-behalf-sessions', employee?.id, year],
    queryFn: () => reviewService.getUserReviewCycle(employee!.id, year, true),
    enabled: !!employee,
  })
  const formal = (sessions || []).filter((s) => PERIOD[String(s.review_type).toLowerCase()])

  const open = async (s: ReviewSessionData) => {
    setTarget(s)
    setOverall(norm(s.initial_rating))
    setKpas({})
    setSaveKeys({})
    setNotes(s.meeting_notes || '')
    setReason('')
    setComplete(true)
    // The year's list carries no KPA ratings, so they come from the check-in itself.
    opening.current = s.id
    setKpaLoad('loading')
    try {
      const recorded = recordedKpas(await reviewService.getReviewSession(s.id))
      if (opening.current !== s.id) return
      setKpas(recorded.ratings)
      setSaveKeys(recorded.keys)
      setKpaLoad('ready')
    } catch {
      if (opening.current === s.id) setKpaLoad('unavailable')
    }
  }

  const close = () => {
    opening.current = null
    setTarget(null)
  }

  const save = async () => {
    if (!target) return
    setSaving(true)
    try {
      const res = await reviewService.recordCheckinOnBehalf(target.id, {
        overall_rating: overall,
        kpa_ratings: Object.fromEntries(KPAS.flatMap((k) => {
          const value = kpas[k.key]
          if (!value) return []
          return (saveKeys[k.key]?.length ? saveKeys[k.key] : [k.key]).map((key) => [key, value])
        })),
        notes: notes.trim() || undefined,
        reason: reason.trim(),
        complete,
      })
      toast.success(res.message)
      close()
      await refetch()
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : "The check-in wasn't recorded. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const locked = (s: ReviewSessionData) => !!s.calibrated_rating || !!s.calibration_locked
  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <ClipboardCheck className="mt-0.5 h-5 w-5 text-heritage-blue" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Record a check-in held outside the system</h3>
            <p className="mt-1 text-sm text-gray-600">
              For a Mid-Year or Year-End check-in that happened outside the system: record the ratings
              and the People Leader&apos;s justification on their behalf. You can record it again to update it,
              until the person is submitted to calibration. Every entry is logged with who recorded it and
              why, and the People Leader and the Team Member see that HR recorded it.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <span className="mb-1 block text-sm font-medium text-gray-700">Team Member</span>
            <EmployeePicker
              selectedEmployees={employee ? [employee] : []}
              onSelectionChange={(list: PickedEmployee[]) => { setEmployee(list[list.length - 1] || null); setTarget(null) }}
              placeholder="Search a team member by name, role, or function..."
            />
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {[currentYear, currentYear - 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        </div>
      </Card>

      {employee && (
        <Card className="p-6">
          {isLoading && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading check-ins…</p>}
          {!isLoading && formal.length === 0 && (
            <p className="text-sm text-gray-500">{employee.name} has no Mid-Year or Year-End check-in for {year}.</p>
          )}
          <ul className="space-y-2">
            {formal.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{PERIOD[String(s.review_type).toLowerCase()]} {year}</span>
                  <span className="text-gray-500"> · {String(s.status).replace(/_/g, ' ')}</span>
                  {s.initial_rating && <span className="text-gray-500"> · rated {RATINGS.find((r) => r.value === norm(s.initial_rating))?.label}</span>}
                </span>
                {locked(s) ? (
                  <span className="flex items-center gap-1 text-xs text-gray-500"><Lock className="h-3.5 w-3.5" /> In calibration: changes go through calibration</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => { void open(s) }}>Record</Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {target && (
        <Card className="space-y-4 p-6">
          <h4 className="font-semibold text-gray-900">
            {PERIOD[String(target.review_type).toLowerCase()]} check-in for {employee?.name}
          </h4>
          {/* The overall rating in a box of its own at the top, then the KPA ratings in a
              group of their own. */}
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-heritage-blue/30 bg-heritage-blue/5 p-4 sm:grid-cols-2">
            <RatingSelect id="overall" label="Overall rating *" value={overall} onChange={setOverall} />
          </div>
          <div role="group" aria-labelledby="kpa-ratings-heading" className="rounded-lg border border-gray-200 p-4">
            <h5 id="kpa-ratings-heading" className="mb-3 text-sm font-semibold text-gray-900">KPA ratings</h5>
            {kpaLoad === 'loading' && (
              <p className="mb-3 flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the KPA ratings already recorded…
              </p>
            )}
            {kpaLoad === 'unavailable' && (
              <p className="mb-3 text-xs text-amber-700">
                The KPA ratings already recorded couldn&apos;t be loaded, so they show as Not rated. Only the
                KPAs you rate here are saved.
              </p>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {KPAS.map((k) => (
                <RatingSelect key={k.key} id={k.key} label={k.label} value={kpas[k.key] || ''}
                  onChange={(v) => setKpas((prev) => ({ ...prev, [k.key]: v }))} />
              ))}
            </div>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">People Leader&apos;s justification{complete ? ' *' : ''}</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={4000}
              placeholder="What was agreed in the check-in, in the People Leader's words." />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Why HR is recording it *</span>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={1000}
              placeholder="e.g. Check-in held in person on 3 Nov; the People Leader asked HR to capture it." />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={complete} onChange={(e) => setComplete(e.target.checked)} />
            Mark the check-in completed
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
            <Button onClick={save}
              disabled={saving || kpaLoad === 'loading' || !overall || reason.trim().length < 5 || (complete && !notes.trim())}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record check-in
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

export default RecordCheckinOnBehalfAdmin
